/**
 * MetricsFirstPaymentCohortsPage — 数据中台 · 付费 cohort（ADM-MTR-S014）
 *
 * 以激活为 cohort，按分组维度（默认语言）看激活用户首次付费落在激活后第几个 UTC 自然日：
 * 激活数 + D0 / D1 / D2 / D3-6 / D7-13 / D14+ 转化率（桶首付人数 ÷ 激活数）+ 累计付费率；
 * 首行为全窗口汇总，可排序、导出 CSV。观察未满的桶标 *（桶人数是下限）。
 * 分组含语言时，各行可展开到地区：展开时以「当前分组 + country」再查一次，子行按父行分组键挂到父行下。
 * 数据走 useMetricsFirstPaymentCohorts（GET /api/v1/metrics/first-payment-cohorts）；应用维度跟随顶栏 App 选择器。
 */

import {
  FIRST_PAYMENT_COHORT_BUCKETS,
  FIRST_PAYMENT_COHORT_MAX_DAYS,
  FIRST_PAYMENT_COHORT_MAX_ROWS,
  type FirstPaymentCohortBucket,
  type FirstPaymentCohortDimension,
} from '@novel-admin/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type {
  FirstPaymentCohortMetricsView,
  FirstPaymentCohortRowView,
  FirstPaymentCohortViewData,
} from '~/data/adapters/metrics-first-payment-cohorts-adapter';
import { useMetricsNestedLevels } from '~/data/metrics-nested-levels';
import {
  fetchMetricsFirstPaymentCohorts,
  metricsFirstPaymentCohortsQueryKey,
  useMetricsFirstPaymentCohorts,
} from '~/data/queries';
import { fmtCountry, fmtLang } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { DateRangeButton } from '~/ui/DatePicker';
import { FilterButton } from '~/ui/FilterButton';
import { TableSkeleton } from '~/ui/Skeleton';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import { MetricsAppFilterButton } from './MetricsAppFilterButton';
import {
  ChipRow,
  ToggleChip,
  daysBetween,
  downloadCsvFile,
  toDay,
} from './conversion-dashboard-shared';
import { MetricsDataTableHeading } from './metrics-data-explanation';
import {
  NESTED_PLACEHOLDER_TEXT,
  type NestedFlatEntry,
  NestedLabelCell,
  flattenNestedRows,
  nestedActiveDeeperLevels,
  nestedDeeperGroupBys,
  nestedRowKey,
} from './metrics-nested-rows';
import { Cal, EmptyBlock, StateGate, type StateGateMode } from './metrics-shared';

type ValueMode = 'rate' | 'count';

/** 可选分组维度；地区不作为芯片，只用于语言行展开 */
const DIMENSIONS: { key: FirstPaymentCohortDimension; label: string }[] = [
  { key: 'language', label: '语言' },
  { key: 'date', label: '激活日期' },
  { key: 'app', label: '应用' },
  { key: 'platform', label: '终端' },
  { key: 'channel', label: '渠道' },
  { key: 'country', label: '地区' },
];

const PLATFORM_LABEL: Record<string, string> = { IOS: 'iOS', ANDROID: 'Android', WEB: 'Web' };
const CHANNEL_TYPE_LABEL: Record<string, string> = {
  NON_ORGANIC: '投放',
  ORGANIC: '自然量',
  ORGANIC_OR_UNKNOWN: '未知',
};

/** 默认近一年（含今天）：生命周期口径，窗口上限 366 天 */
function defaultCohortRange(): [Date, Date] {
  const end = new Date();
  end.setHours(0, 0, 0, 0);
  const start = new Date(end);
  start.setDate(start.getDate() - 364);
  return [start, end];
}

const pct = (value: number | null) => (value === null ? '—' : `${(value * 100).toFixed(2)}%`);

function bucketText(
  metrics: FirstPaymentCohortMetricsView,
  bucket: FirstPaymentCohortBucket,
  mode: ValueMode,
): string {
  const cell = metrics.buckets[bucket];
  return mode === 'rate' ? pct(cell.rate) : cell.count.toLocaleString();
}

function totalText(metrics: FirstPaymentCohortMetricsView, mode: ValueMode): string {
  return mode === 'rate' ? pct(metrics.payerRate) : metrics.payers.toLocaleString();
}

function dimensionLabel(
  dimension: FirstPaymentCohortDimension,
  value: string | null | undefined,
  data: FirstPaymentCohortViewData,
): string {
  if (value === null || value === undefined || value === '')
    return dimension === 'country' ? '未知地区' : '未知';
  switch (dimension) {
    case 'country':
      return fmtCountry(value);
    case 'language':
      return fmtLang(value);
    case 'app':
      return data.options.apps.find((app) => app.id === value)?.name ?? value;
    case 'platform':
      return PLATFORM_LABEL[value] ?? value;
    case 'channel':
      return value === 'organic' ? '自然量' : value;
    default:
      return value;
  }
}

function downloadCsv(
  data: FirstPaymentCohortViewData,
  rows: FirstPaymentCohortRowView[],
  mode: ValueMode,
): void {
  const suffix = mode === 'rate' ? '转化率%' : '人数';
  const header = [
    ...data.groupBy.map((d) => DIMENSIONS.find((x) => x.key === d)?.label ?? d),
    '激活数',
    ...FIRST_PAYMENT_COHORT_BUCKETS.map((b) => `${b.label}${suffix}`),
    `累计${suffix}`,
  ];
  const line = (dims: string[], view: FirstPaymentCohortMetricsView) => [
    ...dims,
    String(view.activations),
    // 观察未满的格子带 *，与表格一致：导出后下限值不会被当成终值
    ...FIRST_PAYMENT_COHORT_BUCKETS.map(
      (b) => `${bucketText(view, b.key, mode)}${view.buckets[b.key].immature ? '*' : ''}`,
    ),
    totalText(view, mode),
  ];
  downloadCsvFile(`first-payment-cohorts-${data.window.from}_${data.window.to}.csv`, [
    header,
    line(
      data.groupBy.map((_, index) => (index === 0 ? '汇总' : '')),
      data.summary,
    ),
    ...rows.map((row) =>
      line(
        data.groupBy.map((d) => dimensionLabel(d, row.dims[d], data)),
        row,
      ),
    ),
  ]);
}

export default function MetricsFirstPaymentCohortsPage() {
  const [appIds, setAppIds] = useState<string[]>([]);
  const [range, setRange] = useState<[Date, Date]>(defaultCohortRange);
  const [channelTypes, setChannelTypes] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [minActivationsInput, setMinActivationsInput] = useState('');
  const [dims, setDims] = useState<FirstPaymentCohortDimension[]>(['language']);
  const [mode, setMode] = useState<ValueMode>('rate');
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);

  const from = toDay(range[0]);
  const to = toDay(range[1]);
  const spanDays = daysBetween(from, to).length;
  const rangeBlocked = spanDays > FIRST_PAYMENT_COHORT_MAX_DAYS;
  const minActivations = /^\d+$/.test(minActivationsInput.trim())
    ? Number(minActivationsInput.trim())
    : null;

  const queryBase = {
    from,
    to,
    appIds,
    channelTypes,
    languages,
  };
  // 分组只先取第一个维度，后面的维度逐级展开时再按前缀取数
  const { data, isLoading, isFetching, isError, refetch } = useMetricsFirstPaymentCohorts({
    ...queryBase,
    groupBy: dims.slice(0, 1),
    minActivations,
    enabled: !rangeBlocked,
  });
  const queryClient = useQueryClient();
  // 展开状态绑定当前筛选 / 分组：条件一变，旧的展开键即失效
  const expandSignature = JSON.stringify([queryBase, dims, minActivations]);
  const [expandedState, setExpandedState] = useState<{ signature: string; keys: Set<string> }>({
    signature: '',
    keys: new Set(),
  });
  const expanded = useMemo(
    () => (expandedState.signature === expandSignature ? expandedState.keys : new Set<string>()),
    [expandedState, expandSignature],
  );
  const setExpanded = (update: Set<string> | ((current: Set<string>) => Set<string>)) =>
    setExpandedState({
      signature: expandSignature,
      keys: typeof update === 'function' ? update(expanded) : update,
    });
  const toggleRow = (key: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  // 更深级不带激活数门槛：门槛只过滤第一级，子行之和 = 父行（dev-docs AD §17 / ADM-MTR-S014）
  const deeperLevels = useMetricsNestedLevels({
    levelGroupBys: nestedDeeperGroupBys(dims),
    activeDeeperLevels: nestedActiveDeeperLevels(expanded, dims.length),
    queryKeyOf: (levelGroupBy) =>
      metricsFirstPaymentCohortsQueryKey({
        ...queryBase,
        groupBy: levelGroupBy as FirstPaymentCohortDimension[],
        minActivations: null,
      }),
    fetchLevel: (levelGroupBy) =>
      fetchMetricsFirstPaymentCohorts({
        ...queryBase,
        groupBy: levelGroupBy as FirstPaymentCohortDimension[],
        minActivations: null,
      }),
    enabled: !rangeBlocked,
  });

  const activeFilterCount =
    appIds.length + channelTypes.length + languages.length + (minActivations !== null ? 1 : 0);
  const resetFilters = () => {
    setAppIds([]);
    setChannelTypes([]);
    setLanguages([]);
    setMinActivationsInput('');
  };

  // 同级行排序：点首列表头按各级自己的维度文本排，点指标列按当前显示的值排（转化率 / 人数跨行不同序，
  // 分母为 0 的行排最后）；未手动排序时保持服务端顺序
  const sortRows = (rows: FirstPaymentCohortRowView[], depth: number) => {
    const levelDimension = dims[depth];
    if (!data || !sort || !levelDimension) return rows;
    const factor = sort.dir === 'asc' ? 1 : -1;
    const sortValue = (row: FirstPaymentCohortRowView): number | string | null => {
      if (sort.key === 'dim') {
        const raw = row.dims[levelDimension];
        return raw === null || raw === undefined ? null : dimensionLabel(levelDimension, raw, data);
      }
      if (sort.key === 'activations') return row.activations;
      if (sort.key === 'total') return mode === 'rate' ? row.payerRate : row.payers;
      const cell = row.buckets[sort.key as FirstPaymentCohortBucket];
      return mode === 'rate' ? cell.rate : cell.count;
    };
    return [...rows].sort((left, right) => {
      const a = sortValue(left);
      const b = sortValue(right);
      if (a === b) return 0;
      if (a === null) return 1;
      if (b === null) return -1;
      const order =
        typeof a === 'string' && typeof b === 'string' ? a.localeCompare(b) : a < b ? -1 : 1;
      return order * factor;
    });
  };
  const flatRows: Array<NestedFlatEntry<FirstPaymentCohortRowView>> = flattenNestedRows({
    dims,
    levels: [
      { rows: data?.rows, isLoading, isError },
      ...deeperLevels.map((level) => ({
        rows: level.data?.rows,
        isLoading: level.isLoading,
        isError: level.isError,
      })),
    ],
    expanded,
    sortRows,
  });
  const truncatedDeeperLevel = deeperLevels.findIndex((level) => level.data?.truncated);
  const anyDeeperFetching = deeperLevels.some((level) => level.isFetching);

  const toggleSort = (key: string) =>
    setSort((current) =>
      current?.key !== key
        ? { key, dir: 'desc' }
        : current.dir === 'desc'
          ? { key, dir: 'asc' }
          : null,
    );

  const gate: StateGateMode = rangeBlocked
    ? 'normal'
    : isLoading
      ? 'loading'
      : isError || !data
        ? 'error'
        : 'normal';

  // 已选中但不在当前候选里的值也要能显示 / 取消（候选随时间窗变化）
  const languageOptions = [...new Set([...(data?.options.languages ?? []), ...languages])];

  return (
    <div className="page" data-screen-label="付费 cohort">
      <div className="page-header">
        <div>
          <h1 className="page-title">付费 cohort</h1>
          <div className="page-sub hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
            激活用户首次付费天数分布 · 按激活 UTC 日
            <Cal
              t="生命周期"
              title="激活 = 首次归因安装（含自然量）；首付 = 激活后最早一笔 Production 到账，天数按 UTC 自然日差；观察至当前时刻。语言优先取投放书语言，无投放书时取用户最近请求语言，再取注册语言"
            />
          </div>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          // 重取期间 data 仍是上一组筛选的占位数据，禁止导出以免导出与当前筛选不符
          disabled={!data || isFetching || anyDeeperFetching || rangeBlocked}
          onClick={async () => {
            if (!data) return;
            // 表格只取到展开的层级；导出要全部维度的明细行，按完整分组再取一次（同参数命中缓存）
            const fullOptions = { ...queryBase, groupBy: dims, minActivations };
            try {
              const full =
                dims.length > 1
                  ? await queryClient.fetchQuery({
                      queryKey: metricsFirstPaymentCohortsQueryKey(fullOptions),
                      queryFn: () => fetchMetricsFirstPaymentCohorts(fullOptions),
                    })
                  : data;
              downloadCsv(full, full.rows, mode);
              toast('已导出 CSV', { tone: 'pos', icon: 'IconDownload' });
            } catch {
              toast('导出失败，请重试', { tone: 'neg', icon: 'IconX' });
            }
          }}
        >
          <Icons.IconDownload size={13} />
          导出
        </button>
      </div>

      <div
        className="card"
        style={{
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <span className="hstack" style={{ gap: 6, fontSize: 12, color: 'var(--fg-muted)' }}>
          <Icons.IconFilter size={13} />
          筛选
        </span>
        <DateRangeButton value={range} onChange={setRange} />
        <MetricsAppFilterButton value={appIds} onChange={setAppIds} />
        <FilterButton
          label="渠道类型"
          options={Object.entries(CHANNEL_TYPE_LABEL).map(([value, label]) => ({ value, label }))}
          value={channelTypes}
          onChange={setChannelTypes}
        />
        <FilterButton
          label="语言"
          options={languageOptions.map((v) => ({ value: v, label: fmtLang(v) }))}
          value={languages}
          onChange={setLanguages}
        />
        <label
          className="hstack"
          style={{ gap: 6, fontSize: 12, color: 'var(--fg-muted)' }}
          title="只显示激活数不低于该值的分组行（不影响汇总行）"
        >
          激活数 ≥
          <input
            className="input mono"
            inputMode="numeric"
            placeholder="不限"
            value={minActivationsInput}
            // 8 位封顶：接口上限 1 亿，超出会被 422 拒绝、整页报加载失败
            onChange={(e) =>
              setMinActivationsInput(e.target.value.replace(/[^\d]/g, '').slice(0, 8))
            }
            style={{ width: 80 }}
          />
        </label>
        {activeFilterCount > 0 && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={resetFilters}>
            <Icons.IconX size={11} />
            重置 {activeFilterCount}
          </button>
        )}
        <div style={{ flex: 1 }} />
        {isFetching && !isLoading && (
          <span style={{ fontSize: 11, color: 'var(--fg-faint)' }}>更新中…</span>
        )}
      </div>

      <div className="card vstack" style={{ padding: '12px 14px', gap: 8 }}>
        <ChipRow label="分组">
          {DIMENSIONS.map((d) => (
            <ToggleChip
              key={d.key}
              label={d.label}
              on={dims.includes(d.key)}
              onClick={() =>
                setDims((current) =>
                  current.includes(d.key)
                    ? // 至少保留一个分组维度：空 group_by 在服务端回落为按语言，芯片状态会与表格不一致
                      current.length === 1
                      ? current
                      : current.filter((x) => x !== d.key)
                    : // 按点击顺序追加：第一个选的是顶层，后选的逐级嵌套在它下面
                      [...current, d.key],
                )
              }
            />
          ))}
        </ChipRow>
        <ChipRow label="显示">
          <ToggleChip label="转化率" on={mode === 'rate'} onClick={() => setMode('rate')} />
          <ToggleChip label="人数" on={mode === 'count'} onClick={() => setMode('count')} />
          {dims.length > 1 && (
            <>
              <div style={{ flex: 1 }} />
              <div className="seg">
                <button
                  type="button"
                  onClick={() =>
                    setExpanded(
                      new Set((data?.rows ?? []).map((row) => nestedRowKey(dims, row, 0))),
                    )
                  }
                >
                  全部展开
                </button>
                <button type="button" onClick={() => setExpanded(new Set())}>
                  全部折叠
                </button>
              </div>
            </>
          )}
        </ChipRow>
      </div>

      <StateGate
        mode={gate}
        refreshing={isFetching}
        skeleton={<TableSkeleton rows={10} cols={9} />}
        errorNote="GET /api/v1/metrics/first-payment-cohorts 加载失败 · 请重试"
        onRetry={refetch}
      >
        {rangeBlocked && (
          <EmptyBlock
            title={`时间窗最多 ${FIRST_PAYMENT_COHORT_MAX_DAYS} 天`}
            note={`当前选择了 ${spanDays} 天，请缩短日期范围`}
          />
        )}
        {!rangeBlocked && data && (
          <CohortTable
            data={data}
            dims={dims}
            entries={flatRows}
            truncatedDeeperLevel={truncatedDeeperLevel}
            mode={mode}
            sort={sort}
            onSort={toggleSort}
            onToggle={toggleRow}
          />
        )}
      </StateGate>
    </div>
  );
}

function BucketCell({
  metrics,
  bucket,
  mode,
}: {
  metrics: FirstPaymentCohortMetricsView;
  bucket: FirstPaymentCohortBucket;
  mode: ValueMode;
}) {
  const cell = metrics.buckets[bucket];
  return (
    <td
      className="num mono"
      title={
        cell.immature
          ? `观察未满：${cell.matured.toLocaleString()} / ${metrics.activations.toLocaleString()} 激活已过完该桶，数值为下限`
          : `${cell.count.toLocaleString()} 人首付落在该桶`
      }
    >
      {bucketText(metrics, bucket, mode)}
      {cell.immature && <span style={{ color: 'var(--fg-faint)' }}>*</span>}
    </td>
  );
}

function CohortTable({
  data,
  dims,
  entries,
  truncatedDeeperLevel,
  mode,
  sort,
  onSort,
  onToggle,
}: {
  data: FirstPaymentCohortViewData;
  dims: FirstPaymentCohortDimension[];
  entries: Array<NestedFlatEntry<FirstPaymentCohortRowView>>;
  /** 第几个更深级被服务端截断（-1 = 都没截断） */
  truncatedDeeperLevel: number;
  mode: ValueMode;
  sort: { key: string; dir: 'asc' | 'desc' } | null;
  onSort: (key: string) => void;
  onToggle: (key: string) => void;
}) {
  const metricColumnCount = FIRST_PAYMENT_COHORT_BUCKETS.length + 2;
  const dimensionHead = dims
    .map((d) => DIMENSIONS.find((x) => x.key === d)?.label ?? d)
    .join(' › ');
  const rowCount = entries.filter((entry) => entry.kind === 'row').length;
  const arrow = (key: string) => (sort?.key === key ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : '');
  const suffix = mode === 'rate' ? '转化率' : '人数';
  const metricHeader = (key: string, label: string, tip: string) => (
    <th
      key={key}
      className="num"
      title={tip}
      style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
      onClick={() => onSort(key)}
    >
      {label}
      {arrow(key)}
    </th>
  );
  const metricCells = (metrics: FirstPaymentCohortMetricsView) => (
    <>
      <td className="num mono">{metrics.activations.toLocaleString()}</td>
      {FIRST_PAYMENT_COHORT_BUCKETS.map((b) => (
        <BucketCell key={b.key} metrics={metrics} bucket={b.key} mode={mode} />
      ))}
      <td className="num mono" style={{ fontWeight: 600 }}>
        {totalText(metrics, mode)}
      </td>
    </>
  );
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <MetricsDataTableHeading title="首次付费时间分布" report="first-payment-cohorts" />
      {(data.truncated || truncatedDeeperLevel >= 0) && (
        <div
          style={{
            padding: '8px 14px',
            fontSize: 12,
            color: 'var(--fg-muted)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          {data.truncated
            ? `分组行超过上限，仅展示前 ${data.rows.length.toLocaleString()} 行；请缩小时间窗或调高激活数门槛。`
            : `第 ${truncatedDeeperLevel + 2} 级明细行超过上限（按激活数保留前 ${FIRST_PAYMENT_COHORT_MAX_ROWS.toLocaleString()} 组），部分父行的下一级明细不全；请缩小时间窗。`}
        </div>
      )}
      {dims.length > 1 && (
        <div style={{ padding: '6px 14px', fontSize: 12, color: 'var(--fg-muted)' }}>
          按「{dimensionHead}」逐级嵌套，点行首箭头展开下一级。
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <Table className="tbl" style={{ tableLayout: 'auto', minWidth: 720 }}>
          <thead>
            <tr>
              <th style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => onSort('dim')}>
                {dimensionHead}
                {arrow('dim')}
              </th>
              {metricHeader('activations', '激活数', '窗口内激活的用户（App × 账号去重）')}
              {FIRST_PAYMENT_COHORT_BUCKETS.map((b) =>
                metricHeader(
                  b.key,
                  `${b.label}${suffix}`,
                  `首付落在激活后第 ${b.label.slice(1)} 天（UTC 自然日）的用户 ÷ 激活数`,
                ),
              )}
              {metricHeader('total', `累计${suffix}`, '有过首付的用户 ÷ 激活数（各桶之和）')}
            </tr>
          </thead>
          <tbody>
            <tr style={{ fontWeight: 600, background: 'var(--bg-subtle)' }}>
              <td>汇总</td>
              {metricCells(data.summary)}
            </tr>
            {entries.map((entry) => {
              if (entry.kind === 'placeholder')
                return (
                  <tr key={entry.key} style={{ background: 'var(--bg-subtle)' }}>
                    <td
                      colSpan={metricColumnCount + 1}
                      style={{
                        paddingLeft: 14 + entry.depth * 20 + 26,
                        fontSize: 12,
                        color: 'var(--fg-muted)',
                      }}
                    >
                      {NESTED_PLACEHOLDER_TEXT[entry.state]}
                    </td>
                  </tr>
                );
              const dimension = dims[entry.depth] as FirstPaymentCohortDimension;
              const label = dimensionLabel(dimension, entry.row.dims[dimension], data);
              return (
                <tr
                  key={entry.key}
                  style={entry.depth > 0 ? { background: 'var(--bg-subtle)' } : undefined}
                >
                  <td className={dimension === 'date' ? 'mono' : undefined}>
                    <NestedLabelCell
                      depth={entry.depth}
                      expandable={entry.expandable}
                      open={entry.open}
                      onToggle={() => onToggle(entry.key)}
                      toggleLabel={`${entry.open ? '折叠' : '展开'} ${label} 的下一级明细`}
                    >
                      {label}
                    </NestedLabelCell>
                  </td>
                  {metricCells(entry.row)}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
      {rowCount === 0 && (
        <EmptyBlock title="当前筛选下没有分组数据" note="换个时间窗、放宽筛选或调低激活数门槛" />
      )}
      <div
        style={{
          padding: '8px 14px',
          fontSize: 11.5,
          color: 'var(--fg-faint)',
          borderTop: '1px solid var(--border)',
        }}
      >
        *
        观察未满：该组有激活用户还没过完这个桶的全部天数，数值是下限。每个用户只计入首付所在的一个桶，
        付费来自 Production 到账结算，观察至{' '}
        <span className="mono">{data.observedUntil.slice(0, 16).replace('T', ' ')} UTC</span>。
      </div>
    </div>
  );
}
